import {
  Component,
  EventEmitter,
  Input,
  Output,
  OnChanges,
  SimpleChanges,
  OnInit,
} from '@angular/core';
import {
  SearchFacetValue,
  SearchFilterFilter,
} from '@app/search/search-api.model';
import { SearchService } from '@app/search/services/search.service';
import { SimpleItemViewModel } from '@app/shared/models/core-view.model';
import { TranslateService } from '@ngx-translate/core';
import { WindowLayoutService } from '@dg/shared-services';
import { SubscriberBaseDirective } from '@app/shared/components/subscriber-base/subscriber-base.directive';
import { debounceTime } from 'rxjs/operators';

export interface ProfileFacetValue {
  id: string;
  name: string;
  count: number;
  filter: boolean;
}

export interface ProfileFacet {
  id: string;
  name: string;
  values: ProfileFacetValue[];
}

export interface ProfileFilter {
  searchTerm: string;
  facets: ProfileFacet[];
  isActive: boolean;
}

/** A filter bar component that can be used on profile tabs with a customizable right section */
@Component({
  selector: 'dgx-profile-filter-bar',
  templateUrl: './profile-filter-bar.component.html',
  styleUrls: ['./profile-filter-bar.component.scss'],
})
export class ProfileFilterBarComponent
  extends SubscriberBaseDirective
  implements OnInit, OnChanges
{
  @Output()
  public filterUpdated: EventEmitter<ProfileFilter> = new EventEmitter();
  @Input() public filter: ProfileFilter = {
    searchTerm: '',
    facets: [],
    isActive: false,
  };
  @Input() public placeholder: string;
  @Input() public minSearchLength? = 1;
  @Input() public includeDefaultFilters = false;

  public i18n = this.translate.instant(['UserController_Type', 'Core_Type']);
  public searchFilters: SearchFilterFilter[] = [];
  public defaultFilterIds: string[];

  public disableStickyHeader =
    this.windowLayoutService.isMobile || this.windowLayoutService.isTablet;
  private selectedFilters: SearchFilterFilter[] = [];

  constructor(
    private translate: TranslateService,
    private searchService: SearchService,
    private windowLayoutService: WindowLayoutService
  ) {
    super();
  }

  public ngOnInit() {
    this.windowLayoutService.windowResize$
      .pipe(debounceTime(300), this.takeUntilDestroyed())
      .subscribe(() => {
        this.disableStickyHeader =
          this.windowLayoutService.isMobile ||
          this.windowLayoutService.isTablet;
      });
  }

  public ngOnChanges(_: SimpleChanges): void {
    if (!!this.filter?.facets) {
      this.defaultFilterIds = this.filter.facets.map((facet) => facet.id);
      this.searchFilters = this.searchService.facetsToFilters(
        this.filter.facets
      );
    } else {
      this.searchFilters = [];
    }
  }

  public search(term: string) {
    this.filter.searchTerm = term;
    this.emitUpdates();
  }

  public facetUpdated() {
    this.emitUpdates();
  }

  public applyFilters(appliedFilters: SearchFilterFilter[]) {
    this.selectedFilters = appliedFilters;
    this.filter.facets = this.getFacetsFromSearchFilters(this.selectedFilters);
    this.facetUpdated();
  }

  public trackFacetById(facet: ProfileFacet) {
    return facet.id;
  }

  public trackValueById(value: ProfileFacetValue) {
    return value.id;
  }

  public getSelectedFacetCount(facet: ProfileFacet) {
    return facet.values.filter((val) => val.filter).length;
  }

  private getFacetsFromSearchFilters(appliedFilters: SearchFilterFilter[]) {
    const facets = [];

    appliedFilters.forEach((filter) => {
      const facet = filter.model;
      const selectedFilterModelIds: string[] = this.findSelectedFilterModelIds(
        filter.subitems
      );

      facet.values = filter.model.values.map((value) => {
        return {
          ...value,
          filter: selectedFilterModelIds.includes(value.id),
        };
      });
      facets.push(facet);
    });

    return facets;
  }

  private findSelectedFilterModelIds = (
    subitems: SimpleItemViewModel<SearchFacetValue>[]
  ) => {
    return subitems
      .filter((subitem) => subitem.isSelected)
      .map((subitem) => subitem.model.id);
  };

  private emitUpdates() {
    this.setFilterStatus();
    this.filterUpdated.emit(this.filter);
  }

  private setFilterStatus() {
    const activeFacets = this.filter.facets.some((facet) => {
      return facet.values.some((val) => val.filter);
    });
    this.filter.isActive = !!this.filter.searchTerm || activeFacets;
    if (!activeFacets) {
      // reset filter counts when there are no active facets
      this.resetFacets();
    }
  }

  private resetFacets() {
    this.filter.facets.forEach((facet) => {
      facet.values.forEach((val) => (val.filter = false));
    });
    this.filter = { ...this.filter };
  }
}
